import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './App.css';
import { LoginWindow } from './auth/frontend/LoginWindow';
import { AuthService } from './auth/AuthService/AuthService';
import { GetService } from './auth/DependencyInjection/DependencyInjection';
import { useObservableValue } from './auth/Observable/Observable';

interface AnalyticsEvent {
  eventName: string;
  eventCount: number;
  groupId: string;
  timestamp: string;
  appVersion?: string;
  properties?: Record<string, any>;
}

interface AnalyticsResponse {
  success: boolean;
  data?: AnalyticsEvent[];
  error?: string;
}

interface ClientsResponse {
  success: boolean;
  data?: string[];
  error?: string;
}

function App() {
  // Auth setup
  const authService = GetService(AuthService);
  const authStatus = useObservableValue(authService.authStatus);
  const authSession = useObservableValue(authService.session);

  // Check if user is authenticated
  const isAuthenticated = authStatus === "SIGNED_IN" || authStatus === "TOKEN_REFRESHED" || authStatus === "USER_UPDATED";

  // Funkcja do obliczania dat aktualnego miesiąca
  const getCurrentMonthDates = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-11 (0=styczeń, 6=lipiec)
    const firstDay = new Date(now.getFullYear(), currentMonth, 1);
    const lastDay = new Date(now.getFullYear(), currentMonth + 1, 0); // Dzień 0 następnego miesiąca = ostatni dzień aktualnego miesiąca

    return {
      startDate: firstDay.toLocaleDateString('en-CA'), // Format YYYY-MM-DD
      endDate: lastDay.toLocaleDateString('en-CA') // Format YYYY-MM-DD
    };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [groupId, setGroupId] = useState('');
  const [startDate, setStartDate] = useState(currentMonthDates.startDate);
  const [endDate, setEndDate] = useState(currentMonthDates.endDate);
  const [eventType, setEventType] = useState('');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [clients, setClients] = useState<string[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [gaStatus, setGaStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [dataNeedsUpdate, setDataNeedsUpdate] = useState(false);

  // Uproszczone wymiary - tylko podstawowe plus opcjonalny appVersion
  const [includeAppVersion, setIncludeAppVersion] = useState(false);

  // Stan dla aktualnie wyświetlanych danych (nie zmienia się przy zmianie formularza)
  const [displayedClientCode, setDisplayedClientCode] = useState('');

  // Stała lista monitorowanych eventów NetDecor
  const allNetDecorEvents = [
    'user_AppInitialized',
    'user_AppSaved',
    'user_LoadProject',
    'user_CreateNewProject',
    'user_CreateNewProject_Interior',
    'user_CreateNewProject_Exterior',
    'PdfGenerated',
    'PdfSaved'
  ];

  // Cykliczne sprawdzanie statusu połączenia z Google Analytics
  useEffect(() => {
    let interval: NodeJS.Timeout;
    const checkHealth = async () => {
      try {
        const res = await fetch('/api/health');
        const data = await res.json();
        if (data.googleAnalytics === 'Connected') {
          if (gaStatus === 'disconnected' || gaStatus === 'checking') {
            // Serwer zaczął działać - pobierz klientów ponownie
            fetchClients();
          }
          setGaStatus('connected');
        } else {
          setGaStatus('disconnected');
        }
      } catch {
        setGaStatus('disconnected');
      }
    };
    checkHealth();
    interval = setInterval(checkHealth, 2000);
    return () => clearInterval(interval);
  }, [gaStatus]);

  // Pobierz listę klientów przy załadowaniu komponentu
  useEffect(() => {
    fetchClients();
  }, []);

  // Pobierz listę klientów ponownie gdy zmieniają się opcje pobierania
  useEffect(() => {
    if (gaStatus === 'connected') {
      fetchClients();
    }
  }, [gaStatus]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      // Pobierz klientów od 2021 roku do chwili obecnej
      const currentDate = new Date().toLocaleDateString('en-CA'); // Format YYYY-MM-DD
      const response = await fetch(`/api/clients?startDate=2021-01-01&endDate=${currentDate}`);
      const result: ClientsResponse = await response.json();

      if (result.success && result.data) {
        setClients(result.data);
        setError(''); // Wyczyść błąd gdy pobieranie się powiedzie
      } else {
        setError('Nie udało się pobrać listy klientów');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem podczas pobierania klientów');
    } finally {
      setLoadingClients(false);
    }
  };

  const getAppVersionDistribution = () => {
    const versionCounts: { [key: string]: number } = {};
    
    analyticsData.forEach(event => {
      if (event.appVersion) {
        const version = event.appVersion; // Backend już mapuje '(not set)' na '5'
        versionCounts[version] = (versionCounts[version] || 0) + event.eventCount;
      }
    });

    return Object.entries(versionCounts).map(([version, count]) => ({
      version,
      count
    }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const response = await fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          groupId,
          startDate,
          endDate,
          eventType: eventType || undefined,
          includeAppVersion,
          eventFilters: allNetDecorEvents,
        }),
      });

      const result: AnalyticsResponse = await response.json();

              if (result.success) {
          if (result.data && result.data.length > 0) {
            setAnalyticsData(result.data);
            setDisplayedClientCode(groupId);
            setDataNeedsUpdate(false);
            setInfo(`Znaleziono ${result.data.length} zdarzeń dla kodu klienta: ${groupId}`);
          } else {
            setAnalyticsData([]);
            setInfo(`Nie znaleziono żadnych danych dla kodu klienta: ${groupId} w okresie od ${startDate} do ${endDate}`);
          }
        } else {
          console.error(`[FRONTEND] Błąd z backendu:`, result.error);
          setError(result.error || 'Wystąpił błąd podczas pobierania danych');
          setAnalyticsData([]);
        }
    } catch (err) {
      setError('Błąd połączenia z serwerem. Sprawdź czy backend jest uruchomiony.');
      setAnalyticsData([]);
    } finally {
      setLoading(false);
    }
  };

  // Funkcja do aktualizacji opcji appVersion
  const handleAppVersionChange = (checked: boolean) => {
    setIncludeAppVersion(checked);
    // Oznacz dane jako nieaktualne po zmianie opcji
    setDataNeedsUpdate(true);
    setInfo('Dane wymagają odświeżenia. Kliknij "Odśwież dane" aby zaktualizować.');
  };

  // Lista monitorowanych zdarzeń (kolejność, klucz, label, kolor linii)
  const monitoredEvents = [
    { key: 'user_AppInitialized', label: 'Inicjalizacja aplikacji', color: '#44BD32' },
    { key: 'user_AppSaved', label: 'Zapisywanie projektu', color: '#FF0000' },
    { key: 'user_LoadProject', label: 'Wczytywanie projektu', color: '#00A8FF' },
    { key: 'user_CreateNewProject', label: 'Tworzenie nowego projektu', color: '#FF9800' },
    { key: 'user_CreateNewProject_Interior', label: 'Tworzenie nowego projektu (Interior)', color: '#ffd54f' },
    { key: 'user_CreateNewProject_Exterior', label: 'Tworzenie nowego projektu (Exterior)', color: '#ffe082' },
    { key: 'PdfGenerated', label: 'Generowanie PDF', color: '#ff00ff' },
    { key: 'PdfSaved', label: 'Zapisywanie PDF', color: '#FFFFFF' }
  ];

  // Stan: które zdarzenia są widoczne na wykresie
  const [visibleEvents, setVisibleEvents] = useState<string[]>([
    'user_AppInitialized',
    'user_AppSaved'
  ]);

  const prepareTimeData = () => {
    const timeData: { [key: string]: { [key: string]: number } } = {};
    
    analyticsData.forEach(event => {
      const date = new Date(event.timestamp).toLocaleDateString('pl-PL');
      if (!timeData[date]) {
        timeData[date] = {};
      }
      timeData[date][event.eventName] = (timeData[date][event.eventName] || 0) + event.eventCount;
    });

    return Object.entries(timeData).map(([date, events]) => {
      const result: any = { date };
      monitoredEvents.forEach(event => {
        result[event.key] = events[event.key] || 0;
      });
      return result;
    }).sort((a, b) => new Date(a.date.split('.').reverse().join('-')).getTime() - new Date(b.date.split('.').reverse().join('-')).getTime());
  };

  // Funkcja do obliczania średniej kroczącej
  const calculateMovingAverage = (data: any[], windowSize: number = 7) => {
    if (data.length < windowSize) return data;

    return data.map((item, index) => {
      const result: any = { date: item.date };
      
      monitoredEvents.forEach(event => {
        const startIndex = Math.max(0, index - windowSize + 1);
        const endIndex = index + 1;
        const window = data.slice(startIndex, endIndex);
        
        const sum = window.reduce((acc, day) => acc + (day[event.key] || 0), 0);
        const average = sum / window.length;
        
        result[event.key] = Math.round(average * 100) / 100; // Zaokrąglenie do 2 miejsc po przecinku
      });
      
      return result;
    });
  };

  const calculateEventStats = () => {
    const stats: { [key: string]: { totalCount: number; averagePerDay: number } } = {};
    
    // Inicjalizuj statystyki dla wszystkich monitorowanych eventów
    monitoredEvents.forEach(event => {
      stats[event.key] = { totalCount: 0, averagePerDay: 0 };
    });

    // Oblicz statystyki
    analyticsData.forEach(event => {
      if (stats[event.eventName]) {
        stats[event.eventName].totalCount += event.eventCount;
      }
    });

    // Oblicz średnią dzienną
    const timeData = prepareTimeData();
    const totalDays = timeData.length || 1;

    Object.keys(stats).forEach(eventName => {
      stats[eventName].averagePerDay = Math.round((stats[eventName].totalCount / totalDays) * 100) / 100;
    });

    return monitoredEvents.map(event => ({
      name: event.key,
      label: event.label,
      color: event.color,
      totalCount: stats[event.key]?.totalCount || 0,
      averagePerDay: stats[event.key]?.averagePerDay || 0
    }));
  };

  const getEventDisplayName = (eventName: string) => {
    const event = monitoredEvents.find(e => e.key === eventName);
    return event ? event.label : eventName;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          color: 'white'
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '2px 0', color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };



  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginWindow />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1 style={{ fontWeight: 700 }}>netDecor Analytics Dashboard</h1>
        <div className="status-indicator">
          <span className={`status-dot ${gaStatus}`}></span>
          <span className="status-text">
            {gaStatus === 'connected' ? 'Połączono z Google Analytics' : 
             gaStatus === 'disconnected' ? 'Brak połączenia z Google Analytics' : 
             'Sprawdzanie połączenia...'}
          </span>
        </div>
        <div className="auth-info">
          <span>Zalogowany jako: {authSession?.user?.email}</span>
          <button 
            onClick={() => authService.signOut()}
            style={{ 
              marginLeft: '10px', 
              padding: '5px 10px', 
              background: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Wyloguj
          </button>
        </div>
      </header>

      <main className="App-main">
        <form onSubmit={handleSubmit} className="analytics-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="groupId">Kod klienta:</label>
              <select
                id="groupId"
                value={groupId}
                onChange={(e) => {
                  setGroupId(e.target.value);
                  if (analyticsData.length > 0) {
                    setDataNeedsUpdate(true);
                    setInfo('Dane wymagają odświeżenia. Kliknij "Odśwież dane" aby zaktualizować.');
                  }
                }}
                required
                disabled={loadingClients}
              >
                <option value="">Wybierz klienta...</option>
                {clients.map((client, index) => (
                  <option key={index} value={client}>
                    {client}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="startDate">Data początkowa:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (analyticsData.length > 0) {
                    setDataNeedsUpdate(true);
                    setInfo('Dane wymagają odświeżenia. Kliknij "Odśwież dane" aby zaktualizować.');
                  }
                }}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">Data końcowa:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (analyticsData.length > 0) {
                    setDataNeedsUpdate(true);
                    setInfo('Dane wymagają odświeżenia. Kliknij "Odśwież dane" aby zaktualizować.');
                  }
                }}
                required
              />
            </div>

            <div className="form-group">
              <button 
                type="submit" 
                disabled={loading}
                className={dataNeedsUpdate ? 'needs-update' : ''}
              >
                {loading ? 'Pobieranie...' : dataNeedsUpdate ? 'Odśwież dane' : analyticsData.length === 0 ? 'Pobierz dane' : 'Odśwież dane'}
              </button>
            </div>
          </div>

          {/* Opcjonalny wymiar appVersion */}
          <div className="form-group">
            <label htmlFor="includeAppVersion" className="checkbox-label">
              <input
                type="checkbox"
                id="includeAppVersion"
                checked={includeAppVersion}
                onChange={(e) => handleAppVersionChange(e.target.checked)}
              />
              Pobierz dane o wersji aplikacji
            </label>
                          <small style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                ℹ️ AppVersion jest dostępne tylko dla wersji aplikacji 6 i nowszych. Starsze wersje nie mają tego parametru.
              </small>
              <small style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                ⚠️ Dane mogą mieć ograniczenia czasowe w zależności od konfiguracji Google Analytics.
              </small>
            </div>
          </form>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {info && (
          <div className={info.includes('Znaleziono') ? 'success-message' : 'info-message'}>
            {info}
          </div>
        )}

        {analyticsData.length > 0 && (
          <div className="results-section">

            <h2>Wykres aktywności klienta: {displayedClientCode}</h2>
            {/* Wykres kołowy rozkładu wersji aplikacji */}
            {getAppVersionDistribution().length > 1 && (
              <div className="charts-container" style={{ marginBottom: '2rem' }}>
                <div className="chart-card" style={{ flex: '1', minWidth: '300px' }}>
                  <h3>Rozkład eventów wg wersji aplikacji</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getAppVersionDistribution()}
                        dataKey="count"
                        nameKey="version"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        label={({ version, count }) => `${version}: ${count}`}
                      >
                        {getAppVersionDistribution().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Wykres liniowy aktywności w czasie */}
            <div className="charts-container">
              <div className="chart-card">
                <h3>Aktywność w czasie (dane dzienne)</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={prepareTimeData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {monitoredEvents
                      .filter(event => visibleEvents.includes(event.key))
                      .map((event, index) => (
                        <Line
                          key={event.key}
                          type="monotone"
                          dataKey={event.key}
                          stroke={event.color}
                          strokeWidth={2}
                          dot={false}
                          name={event.label}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Wykres z średnią kroczącą 30-dniową */}
            <div className="charts-container">
              <div className="chart-card">
                <h3>Aktywność w czasie (średnia krocząca 30-dniowa)</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={calculateMovingAverage(prepareTimeData(), 30)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {monitoredEvents
                      .filter(event => visibleEvents.includes(event.key))
                      .map((event, index) => (
                        <Line
                          key={event.key}
                          type="monotone"
                          dataKey={event.key}
                          stroke={event.color}
                          strokeWidth={4}
                          dot={false}
                          name={`${event.label} (średnia 30-dniowa)`}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tabela statystyk eventów */}
            <div className="stats-section">
              <h3>Statystyki eventów</h3>
              <div className="stats-table-container">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Widoczność</th>
                      <th>Typ eventu</th>
                      <th>Łączna liczba</th>
                      <th>Średnia dzienna</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculateEventStats().map((stat, index) => (
                      <tr key={index}>
                        <td>
                          <label className="table-checkbox-label" style={{ color: stat.color, borderColor: stat.color }}>
                            <input
                              type="checkbox"
                              checked={visibleEvents.includes(stat.name)}
                              onChange={() => {
                                setVisibleEvents(prev =>
                                  prev.includes(stat.name)
                                    ? prev.filter(k => k !== stat.name)
                                    : [...prev, stat.name]
                                );
                              }}
                              style={{ accentColor: stat.color }}
                            />
                          </label>
                        </td>
                        <td>
                          <div className="event-name-cell">
                            <span className="event-color-dot" style={{ backgroundColor: stat.color }}></span>
                            <span className="event-name" style={{ color: stat.color, fontWeight: 600 }}>{stat.label}</span>
                          </div>
                        </td>
                        <td>{stat.totalCount}</td>
                        <td>{stat.averagePerDay}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kafelki z danymi eventów */}
            <div className="stats-section">
              <h3>Dane eventów {visibleEvents.length === monitoredEvents.length ? '(wszystkie)' : `(tylko ${visibleEvents.length} z ${monitoredEvents.length})`}</h3>
              <div className="events-grid">
                {(() => {
                  // Grupuj eventy po dacie i wersji (tylko jeśli includeAppVersion jest włączone)
                  const eventsByDateAndVersion: { [key: string]: { [version: string]: AnalyticsEvent[] } } = {};
                  
                  analyticsData
                    .filter(event => monitoredEvents.some(e => e.key === event.eventName))
                    .filter(event => visibleEvents.includes(event.eventName))
                    .forEach(event => {
                      const date = new Date(event.timestamp).toLocaleDateString('pl-PL');
                      
                      if (includeAppVersion) {
                        // Jeśli włączona opcja appVersion, grupuj po wersji
                        const version = event.appVersion || '5';
                        
                        if (!eventsByDateAndVersion[date]) {
                          eventsByDateAndVersion[date] = {};
                        }
                        if (!eventsByDateAndVersion[date][version]) {
                          eventsByDateAndVersion[date][version] = [];
                        }
                        eventsByDateAndVersion[date][version].push(event);
                      } else {
                        // Jeśli wyłączona opcja appVersion, grupuj tylko po dacie
                        if (!eventsByDateAndVersion[date]) {
                          eventsByDateAndVersion[date] = {};
                        }
                        if (!eventsByDateAndVersion[date]['all']) {
                          eventsByDateAndVersion[date]['all'] = [];
                        }
                        eventsByDateAndVersion[date]['all'].push(event);
                      }
                    });

                  // Sortuj daty malejąco
                  const sortedDates = Object.keys(eventsByDateAndVersion).sort((a, b) => 
                    new Date(b.split('.').reverse().join('-')).getTime() - new Date(a.split('.').reverse().join('-')).getTime()
                  );

                  return sortedDates.map((date, dateIndex) => {
                    const totalEvents = Object.values(eventsByDateAndVersion[date]).flat().length;
                    const sortedVersions = Object.keys(eventsByDateAndVersion[date]).sort((a, b) => {
                      // Sortuj wersje: najpierw numeryczne, potem tekstowe
                      const aNum = parseFloat(a);
                      const bNum = parseFloat(b);
                      if (!isNaN(aNum) && !isNaN(bNum)) {
                        return bNum - aNum; // Malejąco
                      }
                      return a.localeCompare(b);
                    });

                    return (
                      <div key={dateIndex} className="event-day-card">
                        <div className="event-day-header">
                          <h4>{date}</h4>
                          <span className="event-count-badge">
                            {totalEvents} eventów
                          </span>
                        </div>
                        <div className="event-day-content">
                          {sortedVersions.map((version, versionIndex) => (
                            <div key={versionIndex} className="version-group">
                              {includeAppVersion && (
                                <div className="version-header">
                                  <span className="version-label">Wersja {version}</span>
                                  <span className="version-count">
                                    {eventsByDateAndVersion[date][version].length} eventów
                                  </span>
                                </div>
                              )}
                              <div className="version-events">
                                {eventsByDateAndVersion[date][version].map((event, eventIndex) => (
                                  <div key={eventIndex} className="event-item">
                                    <div className="event-item-header">
                                      <span 
                                        className="event-name"
                                        style={{
                                          color: monitoredEvents.find(e => e.key === event.eventName)?.color || '#95a5a6'
                                        }}
                                      >
                                        {getEventDisplayName(event.eventName)}
                                      </span>
                                      <span className="event-count">{event.eventCount}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
